import { Avatar } from '@mui/material';
import { Box } from '@mui/system';
import { resendInvitationRequest } from 'api/requests/employee-requests';
import UPButton from 'components/button';
import { CompanyRole } from 'constants/CompanyRole';
import useReasonForEmploymentTranslation from 'hooks/useReasonForEmploymentTranslation';
import { toNumber } from 'lodash';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store';
import { impersonateEmployeeAction } from 'store/actions/auth-actions';
import { setEmployeeError } from 'store/actions/employee-actions';
import { profileSelector } from 'store/selectors/profile-selectors';
import {
  EMPLOYEES__COPY_LINK_BUTTON,
  EMPLOYEES__EXTRA_FULL,
  EMPLOYEES__FLEXI_FULL,
  EMPLOYEES__LABOURER_FULL,
  EMPLOYEES__PERMANENT,
  EMPLOYEES__REINVITE_BUTTON,
  EMPLOYEES__SERVANT_FULL,
  EMPLOYEES__STUDENT_FULL,
  GENERAL__EDIT,
  GENERAL__ERROR_MESSAGE,
  GENERAL__IMPERSONATE,
} from 'translations/constants';
import Lang from 'types/Lang';
import { getUpDropdownItemsWithIdAsValueFromEnum } from 'utils/helpers';
import { getEmployeeStatusTag } from './helpers';
import { ReactComponent as CopyToClipboard } from '../../assets/icons/CopyToClipboard.svg';
import { ReactComponent as EditEmployeeAvatar } from '../../assets/icons/EditEmployeeAvatar.svg';
import { ReactComponent as InfoIcon } from '../../assets/icons/EmployeeInfoIcon.svg';
import { ReactComponent as EmployeeRequiresAction } from '../../assets/icons/EmployeeRequiresAction.svg';
import {
  CardAvatar,
  CardContactGroup,
  CardContent,
  CardMetaGroup,
  CardMetaLine,
  ContactDetails,
  EmployeeCardStyled,
  EmployeeName,
  GreyedOutAvatar,
  InviteActionButton,
  InviteActionsContainer,
  MoreContent,
  PersonalDetails,
  RequiresActionContainer,
  StatusTag,
  StatusTagContainer,
  StatusTagIcon,
} from './styles';
import { EmployeeCardProps } from './types';

const EmployeeCard = (props: EmployeeCardProps) => {
  const { employee, onClickEdit, companyId, setCopied, setInviteSent } = props;

  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const profile = useAppSelector(profileSelector);
  const isAdmin = profile.role === CompanyRole.ADMIN;
  const isManager = ['Company manager', 'Store/location manager', 'HR manager'].includes(profile?.role);
  const [displayMore, setDisplayMore] = useState<boolean>(false);

  const isInvited = employee.age === 0;
  const isTerminated = employee.terminated;
  const statusTag = getEmployeeStatusTag(employee, isInvited);

  const reasonForEmployment = useReasonForEmploymentTranslation(employee.reasonForEmployment);

  const findRightTranslation = (workerclass: string) => {
    switch (workerclass) {
      case 'Student':
        return t(EMPLOYEES__STUDENT_FULL);
      case 'Flexi-job':
        return t(EMPLOYEES__FLEXI_FULL);
      case 'Extra':
        return t(EMPLOYEES__EXTRA_FULL);
      case 'Labourer':
        return t(EMPLOYEES__LABOURER_FULL);
      case 'Permanent':
        return t(EMPLOYEES__PERMANENT);
      default:
        return t(EMPLOYEES__SERVANT_FULL);
    }
  };

  const ResendInviteEmail = async () => {
    try {
      await resendInvitationRequest({ emailAddress: employee.emailAddress, companyId });
      setInviteSent(true);
    } catch (error: any) {
      dispatch(setEmployeeError(error?.response?.data?.error ?? GENERAL__ERROR_MESSAGE));
    }
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(employee.invitationLink);
    setCopied(true);
  };

  const onClickImpersonate = useCallback(
    (employeeId: number) => {
      dispatch(impersonateEmployeeAction({ employeeId, companyId: toNumber(companyId) }));
    },
    [companyId, dispatch],
  );

  return (
    <EmployeeCardStyled
      onClick={() => {
        if (!isInvited) setDisplayMore(prevState => !prevState);
      }}
    >
      {!employee.canSkipApproval && (
        <RequiresActionContainer>
          <EmployeeRequiresAction />
        </RequiresActionContainer>
      )}
      {displayMore ? (
        <MoreContent>
          <EmployeeName>{employee.companyName}</EmployeeName>
          <PersonalDetails>
            {employee.nationality} | {employee.language.toUpperCase()}
          </PersonalDetails>
          <PersonalDetails>
            {employee.address.streetName} {employee.address.streetNumber}, Bus no. {employee.address.busNumber},{' '}
            {employee.address.postalCode}, {employee.address.city}
          </PersonalDetails>
          <PersonalDetails>{reasonForEmployment}</PersonalDetails>
          {isAdmin && (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
              <UPButton text={t(GENERAL__EDIT)} onClick={() => onClickEdit(employee.id)} />
              <UPButton
                text={t(GENERAL__IMPERSONATE)}
                onClick={() => onClickImpersonate(employee.id)}
                disabled={employee.terminated}
              />
            </Box>
          )}

          {isManager && (
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
              <InfoIcon onClick={() => onClickEdit(employee.id)} />
            </Box>
          )}
        </MoreContent>
      ) : (
        <CardContent>
          <CardAvatar>
            {!isTerminated && !employee.pictureUpload.includes(null) ? (
              <Avatar src={employee.pictureUpload} sx={{ width: '56px', height: '56px' }} />
            ) : (
              <GreyedOutAvatar isGreyedOut={isTerminated}>
                <EditEmployeeAvatar width={56} height={56} />
              </GreyedOutAvatar>
            )}
          </CardAvatar>
          <EmployeeName>
            {employee.firstName} {employee.lastName}
          </EmployeeName>
          {!isInvited && (
            <CardMetaGroup>
              <CardMetaLine>{`${employee.gender} | ${employee.age} | ${findRightTranslation(
                employee.workerclass,
              )}`}</CardMetaLine>
            </CardMetaGroup>
          )}
          <CardContactGroup>
            {!isInvited && <ContactDetails>{employee.mobileNumber}</ContactDetails>}
            <ContactDetails>{employee.emailAddress}</ContactDetails>
          </CardContactGroup>
          {isInvited && (
            <InviteActionsContainer>
              <InviteActionButton text={t(EMPLOYEES__REINVITE_BUTTON)} onClick={ResendInviteEmail} />
              <InviteActionButton text={t(EMPLOYEES__COPY_LINK_BUTTON)} onClick={handleCopyToClipboard} />
            </InviteActionsContainer>
          )}
          <StatusTagContainer>
            <StatusTag tone={statusTag.tone}>
              {statusTag.icon && (
                <StatusTagIcon>
                  <statusTag.icon />
                </StatusTagIcon>
              )}
              {t(statusTag.labelKey)}
            </StatusTag>
          </StatusTagContainer>
        </CardContent>
      )}
    </EmployeeCardStyled>
  );
};

export default EmployeeCard;
